import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Heart, Calendar, Pill, Activity } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TherapyTokenGift from "./TherapyTokenGift";
import AppointmentManager from "./AppointmentManager";
import { TherappiSupportCard } from "../journey/TherappiSupportCard";
import { NutritionalGuidanceCard } from "../journey/NutritionalGuidanceCard";
import MenstrualCalendar from "../MenstrualCalendar";
import { useTrackerData } from "@/hooks/useTrackerData";
import { calculateLunarPhase } from "@/utils/lunarPhases";

interface IVFTrackerProps {
  onBack: () => void;
}

const IVF_SUPPORT_ACTIVITIES = [
  { id: 'massage', label: 'Massage Therapy' },
  { id: 'breathwork', label: 'Breathwork' },
  { id: 'meditation', label: 'Meditation' },
  { id: 'acupuncture', label: 'Acupuncture' },
  { id: 'kinesiology', label: 'Kinesiology' },
  { id: 'supplements', label: 'Supplements' },
  { id: 'visualisation', label: 'Visualisation' },
  { id: 'nutrition_plan', label: 'Nutrition Plan' },
  { id: 'chinese_medicine', label: 'Chinese Medicine' },
  { id: 'conscious_connection', label: 'Conscious Connection' },
  { id: 'future_timeline_journey', label: 'Future Timeline Journey' },
  { id: 'exercise', label: 'Exercise / Movement' }
];

export default function IVFTracker({ onBack }: IVFTrackerProps) {
  const { toast } = useToast();
  const { loadTrackerData } = useTrackerData();
  const [loading, setLoading] = useState(false);
  const [cycleDay, setCycleDay] = useState<number>(1);
  const [medications, setMedications] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [supportActivities, setSupportActivities] = useState<Record<string, boolean>>({});

  // Load today's data on mount
  useEffect(() => {
    loadTodayData();
  }, []);

  const loadTodayData = async () => {
    const data = await loadTrackerData('daily_checkins');
    if (data) {
      if (data.ivf_cycle_day) setCycleDay(data.ivf_cycle_day);
      if (data.ivf_notes) setNotes(data.ivf_notes);
      if (data.ivf_medications) setMedications(data.ivf_medications.join('\n'));
      if (data.ivf_support_activities) setSupportActivities(data.ivf_support_activities);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const medicationsArray = medications.split('\n').filter(m => m.trim());
      
      // Calculate current lunar phase
      const today = new Date();
      const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0));
      const lunarPhase = calculateLunarPhase(utcToday);

      const { error } = await supabase
        .from('daily_checkins')
        .upsert({
          user_id: user.id,
          date: new Date().toISOString().split('T')[0],
          ivf_cycle_active: true,
          ivf_cycle_day: cycleDay,
          ivf_medications: medicationsArray,
          ivf_notes: notes,
          ivf_support_activities: supportActivities,
          lunar_phase: lunarPhase.phase
        }, {
          onConflict: 'user_id,date'
        });

      if (error) throw error;

      toast({
        title: "IVF Journey Updated",
        description: "Your IVF cycle information has been saved."
      });
    } catch (error) {
      console.error('Error saving IVF data:', error);
      toast({
        title: "Error",
        description: "Failed to save IVF data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold">IVF Journey Tracker</h2>
          <p className="text-muted-foreground">Track your IVF cycle with compassion and care</p>
        </div>
      </div>

      <Card className="p-6 bg-primary/5 border-primary/20">
        <h3 className="font-semibold mb-2">Supporting Your IVF Journey</h3>
        <p className="text-sm text-muted-foreground">
          This tracker helps you monitor your IVF cycle, medications, and supportive activities. 
          The journey can be emotionally and physically demanding-tracking helps you stay organised 
          and gives you insight into what supports your wellbeing during this time.
        </p>
      </Card>

      <TherapyTokenGift journeyType="ivf" />

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="calendar">
            <Calendar className="h-4 w-4 mr-2" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="cycle">
            <Calendar className="h-4 w-4 mr-2" />
            Today
          </TabsTrigger>
          <TabsTrigger value="medications">
            <Pill className="h-4 w-4 mr-2" />
            Medications
          </TabsTrigger>
          <TabsTrigger value="support">
            <Heart className="h-4 w-4 mr-2" />
            Support
          </TabsTrigger>
          <TabsTrigger value="activities">
            <Activity className="h-4 w-4 mr-2" />
            Activities
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="space-y-4">
          <MenstrualCalendar
            onSaveCycleSettings={async () => {
              toast({
                title: "Cycle settings saved",
                description: "Your menstrual cycle data is saved across all journeys."
              });
            }}
          />
          <AppointmentManager journeyType="ivf" />
        </TabsContent>

        <TabsContent value="cycle" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cycle Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cycleDay">IVF Cycle Day</Label>
                <Input
                  id="cycleDay"
                  type="number"
                  min="1"
                  value={cycleDay}
                  onChange={(e) => setCycleDay(parseInt(e.target.value) || 1)}
                  placeholder="Enter cycle day"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes & Observations</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="How are you feeling today? Any symptoms or observations?"
                  rows={4}
                />
              </div>
              <AppointmentManager journeyType="ivf" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="medications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Medications & Protocol</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="medications">Today's Medications</Label>
                <Textarea
                  id="medications"
                  value={medications}
                  onChange={(e) => setMedications(e.target.value)}
                  placeholder="List medications (one per line)&#10;e.g., Gonal-F 150 IU - 8am&#10;Menopur 75 IU - 8pm"
                  rows={8}
                />
                <p className="text-xs text-muted-foreground">
                  Enter each medication on a new line with dosage and time
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="support" className="space-y-4">
          <TherappiSupportCard journeyType="ivf" />
          <NutritionalGuidanceCard focusArea="fertility" />
        </TabsContent>

        <TabsContent value="activities" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Supportive Activities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Track complementary activities that support your IVF journey
              </p>
              <div className="grid grid-cols-2 gap-4">
                {IVF_SUPPORT_ACTIVITIES.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={activity.id}
                      checked={supportActivities[activity.id] || false}
                      onCheckedChange={(checked) => 
                        setSupportActivities(prev => ({ ...prev, [activity.id]: checked as boolean }))
                      }
                    />
                    <Label htmlFor={activity.id} className="text-sm cursor-pointer">
                      {activity.label}
                    </Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save Today's Entry"}
        </Button>
      </div>
    </div>
  );
}
